import * as XLSX from 'xlsx';
import { getCountries, getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { SmsParts, smsParts } from '../../../utils/sms';
import {
  countriesOperator,
  parseFlexiblePhoneNumber,
  sanitizePhoneNumber,
} from '../../../utils/utils';
import {
  ISmartBroadcastPhone,
  ISmartBroadcastRendered,
  SmartBroadcastPhoneState,
  SmartBroadcastRename,
} from '../../types/smartBroadcast';
import { stripTextMarker } from '../../../utils/csv';

/**
 * Pure helpers for Smart Broadcast. Everything here mirrors what the server
 * does with the uploaded file, so the page can show each recipient the exact
 * message they will get before anything is sent.
 */

/**
 * `{{ field }}` as the server reads it. Same pattern as ATTRIBUTE_PLACEHOLDER
 * in utils.ts and SmartBroadcastServiceImpl: letters, digits, underscores and
 * spaces only. Keep the three in step.
 */
export const MERGE_FIELD = /\{\{\s*([a-zA-Z0-9_ ]+?)\s*\}\}/g;
const SAFE_FIELD = /^[A-Za-z0-9_ ]+$/;

/** A value Excel has already shortened, such as 2.67712E+11. */
const EXCEL_SHORTENED = /^[+-]?\d+(?:[.,]\d+)?e\+?\d+$/i;

/** Largest file the browser reads. Bigger sheets should be split. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** Rows a single broadcast takes. */
export const MAX_ROWS = 50_000;
/**
 * Sheet rows read at most: the header plus one row past the limit, so an
 * oversized sheet is caught without reading a million formatted-but-empty
 * rows. SheetJS counts sheet rows here (blank and title rows included).
 */
const SHEET_ROW_CAP = MAX_ROWS + 2;
/** Columns read from a sheet; formatting can stretch a sheet's range far past its data. */
const MAX_COLUMNS = 500;
/**
 * Spring's default multipart limit. The server sets none of its own, so an
 * upload over 1 MB is likely to be turned away.
 */
export const UPLOAD_SOFT_LIMIT = 1024 * 1024;

// ---- Sheet reading ----------------------------------------------------------

export interface SheetData {
  headers: string[];
  rows: string[][];
  /** Row number of each data row as the spreadsheet shows it (1-based). */
  rowNumbers: number[];
  /** Per column: long numbers read back in full. */
  restored: number[];
  /** Per column: values stored as shortened text (digits lost). */
  shortened: number[];
  /** Blank rows between data rows that were left out. */
  skippedRows: number;
  /** Columns with no header and no values that were dropped. */
  droppedColumns: number;
  /** Data reached the last row read, so the sheet may go on past the row cap. */
  truncated?: boolean;
}

export function readWorkbook(buffer: ArrayBuffer): XLSX.WorkBook {
  return XLSX.read(new Uint8Array(buffer), {
    type: 'array',
    // Text files (CSV, TSV) keep every value exactly as typed: leading zeros,
    // long numbers and dates are not reinterpreted. Ignored for Excel files.
    raw: true,
    sheetRows: SHEET_ROW_CAP,
    cellFormula: false,
    cellHTML: false,
    cellStyles: false,
  });
}

/** Rough data-row count per sheet, for the sheet picker. */
export function sheetSize(workbook: XLSX.WorkBook, name: string): number {
  const sheet = workbook.Sheets[name];
  const ref = (sheet?.['!fullref'] as string | undefined) ?? sheet?.['!ref'];
  if (!ref) return 0;
  const range = XLSX.utils.decode_range(ref);
  return Math.max(0, range.e.r - range.s.r);
}

/** The first sheet that holds a header and at least one data row. */
export function firstUsefulSheet(workbook: XLSX.WorkBook): string {
  return workbook.SheetNames.find((name) => sheetSize(workbook, name) > 0) ?? workbook.SheetNames[0] ?? '';
}

/**
 * Reads one sheet as text. Excel shows long numbers in General format as
 * 2.67712E+11; the cell still holds every digit, so those are read back in
 * full instead of the shortened text the old upload sent.
 */
export function readSheet(workbook: XLSX.WorkBook, name: string): SheetData {
  const empty: SheetData = {
    headers: [],
    rows: [],
    rowNumbers: [],
    restored: [],
    shortened: [],
    skippedRows: 0,
    droppedColumns: 0,
  };
  const sheet = workbook.Sheets[name];
  if (!sheet || !sheet['!ref']) return empty;

  const range = XLSX.utils.decode_range(sheet['!ref']);
  const width = Math.min(range.e.c - range.s.c + 1, MAX_COLUMNS);
  const letters = Array.from({ length: width }, (_, i) => XLSX.utils.encode_col(range.s.c + i));
  const restoredAll = new Array<number>(width).fill(0);

  const lines: { values: string[]; sheetRow: number }[] = [];
  let skippedRows = 0;
  for (let r = range.s.r; r <= range.e.r; r++) {
    const values = new Array<string>(width);
    let any = false;
    for (let c = 0; c < width; c++) {
      const read = cellText(sheet[`${letters[c]}${r + 1}`] as XLSX.CellObject | undefined);
      values[c] = read.text;
      if (read.restored) restoredAll[c]++;
      if (read.text) any = true;
    }
    if (any) lines.push({ values, sheetRow: r + 1 });
    else if (lines.length) skippedRows++;
  }
  if (!lines.length) return empty;

  // Trailing blank rows are not worth a mention.
  const lastRow = lines[lines.length - 1].sheetRow;
  skippedRows = Math.max(0, skippedRows - (range.e.r + 1 - lastRow));

  const [head, ...body] = lines;
  // Keep a column if it has a header or any value.
  const keep: number[] = [];
  for (let c = 0; c < width; c++) {
    if (head.values[c] || body.some((line) => line.values[c])) keep.push(c);
  }

  const shortened = keep.map(() => 0);
  const rows = body.map((line) =>
    keep.map((c, k) => {
      const value = line.values[c];
      if (value && EXCEL_SHORTENED.test(value)) shortened[k]++;
      return value;
    })
  );

  return {
    headers: keep.map((c) => head.values[c]),
    rows,
    rowNumbers: body.map((line) => line.sheetRow),
    restored: keep.map((c) => restoredAll[c]),
    shortened,
    skippedRows,
    droppedColumns: width - keep.length,
    // Rows past the cap were never read. Data in the last row read means
    // the sheet may go on, whatever the count of data rows above it.
    truncated: lastRow >= SHEET_ROW_CAP,
  };
}

function cellText(cell: XLSX.CellObject | undefined): { text: string; restored: boolean } {
  if (!cell || cell.t === 'z') return { text: '', restored: false };
  if (cell.t === 'n' && typeof cell.v === 'number' && cell.w && /e\+/i.test(cell.w)) {
    const full = Number.isSafeInteger(cell.v) ? String(cell.v) : '';
    // Only a real gain counts: if the stored number has no more digits than
    // the short form shows, the digits were lost before upload.
    if (full && significantDigits(full) > significantDigits(cell.w)) {
      return { text: full, restored: true };
    }
  }
  let text = '';
  try {
    text = XLSX.utils.format_cell(cell) ?? '';
  } catch {
    text = cell.v === undefined || cell.v === null ? '' : String(cell.v);
  }
  // Our own CSV downloads mark +numbers with a leading apostrophe; drop it.
  return { text: stripTextMarker(String(text).trim()), restored: false };
}

function significantDigits(value: string): number {
  const mantissa = value.split(/e/i)[0].replace(/\D/g, '');
  return mantissa.replace(/^0+/, '').replace(/0+$/, '').length;
}

// ---- Field names --------------------------------------------------------------

export interface FieldName {
  field: string;
  renamed: SmartBroadcastRename;
}

/**
 * The merge field each header becomes. Headers the server can already match
 * keep their name. Blank, duplicate or unmatchable ones ("Amount (BWP)",
 * "e-mail", "Prénom") get a safe name, which is also what the uploaded file's
 * header row uses, so every column can be merged.
 */
export function fieldNames(headers: string[]): FieldName[] {
  const taken = new Set<string>();
  return headers.map((header, index) => {
    const trimmed = (header ?? '').trim().replace(/\s+/g, ' ');
    let base = trimmed;
    let renamed: SmartBroadcastRename = null;

    if (!trimmed) {
      base = `Column_${index + 1}`;
      renamed = 'blank';
    } else if (!SAFE_FIELD.test(trimmed)) {
      base = trimmed
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .join('_');
      if (!base) base = `Column_${index + 1}`;
      renamed = 'unsafe';
    }

    let field = base;
    let n = 2;
    while (taken.has(field.toLowerCase())) {
      field = `${base}_${n++}`;
      renamed = renamed ?? 'duplicate';
    }
    taken.add(field.toLowerCase());
    return { field, renamed };
  });
}

// ---- Phone numbers ------------------------------------------------------------

export function readPhone(
  raw: string,
  country?: CountryCode
): Omit<ISmartBroadcastPhone, 'repeatOf'> {
  const value = (raw ?? '').trim();
  if (!value) return { raw: '', number: '', state: 'empty' };
  if (EXCEL_SHORTENED.test(value)) return { raw: value, number: value, state: 'shortened' };
  const cleaned = sanitizePhoneNumber(value);
  const parsed = cleaned ? parseFlexiblePhoneNumber(cleaned, country) : undefined;
  return parsed
    ? { raw: value, number: parsed.number, state: 'valid' }
    : { raw: value, number: cleaned, state: 'invalid' as SmartBroadcastPhoneState };
}

const PHONE_HEADER = /phone|mobile|msisdn|cell|tel|whatsapp|contact|number|sms/i;

/** The column most of whose values read as phone numbers. */
export function guessPhoneColumn(
  headers: string[],
  rows: string[][],
  country?: CountryCode
): { index: number; share: number } | null {
  let best = { index: -1, share: 0, score: 0 };
  for (let index = 0; index < headers.length; index++) {
    let tried = 0;
    let valid = 0;
    for (let r = 0; r < rows.length && tried < 200; r++) {
      const value = rows[r][index];
      if (!value) continue;
      tried++;
      if (readPhone(value, country).state === 'valid') valid++;
    }
    if (!tried) continue;
    const share = valid / tried;
    const score = share + (PHONE_HEADER.test(headers[index]) ? 0.15 : 0);
    if (share >= 0.5 && score > best.score) best = { index, share, score };
  }
  return best.index >= 0 ? { index: best.index, share: best.share } : null;
}

export interface CountryOption {
  code: CountryCode;
  name: string;
  dial: string;
}

let countryCache: CountryOption[] | null = null;

/** Every country libphonenumber knows, by name. */
export function countryOptions(): CountryOption[] {
  if (countryCache) return countryCache;
  countryCache = getCountries()
    .map((code) => ({
      code,
      name: countriesOperator.getName(code, 'en') || code,
      dial: `+${getCountryCallingCode(code)}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return countryCache;
}

export function toCountryCode(value: string | null | undefined): CountryCode | '' {
  const code = (value ?? '').trim().toUpperCase();
  return code && isSupportedCountry(code) ? (code as CountryCode) : '';
}

// ---- Messages -----------------------------------------------------------------

/**
 * The message one row receives. Like the server, a placeholder stays as
 * written ({{firstName}}) when the column is unknown or the cell is empty.
 */
export function renderMessage(
  template: string,
  value: (field: string) => string | undefined,
  fallbacks: Record<string, string>
): ISmartBroadcastRendered {
  const empty: string[] = [];
  const unknown: string[] = [];
  const text = (template ?? '').replace(MERGE_FIELD, (match, key: string) => {
    const cell = value(key);
    if (cell === undefined) {
      if (!unknown.includes(key)) unknown.push(key);
      return match;
    }
    const filled = cell || (fallbacks[key] ?? '').trim();
    if (!filled) {
      if (!empty.includes(key)) empty.push(key);
      return match;
    }
    return filled;
  });
  return { text, empty, unknown };
}

/**
 * Exact SMS parts for a finished message. smsParts() treats {{field}} as a
 * stand-in of average length; a placeholder left in a sent message is real
 * text, so braces are swapped for square brackets first. Both are GSM
 * extension characters (two units each) and one UTF-16 unit each, so the
 * count is unchanged in either encoding.
 */
export function exactParts(text: string): SmsParts {
  return smsParts((text ?? '').replace(/\{/g, '[').replace(/\}/g, ']'));
}

/** Byte size of text once UTF-8 encoded. */
export function utf8Bytes(text: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
  return text.length;
}
